/**
 * El BLOQUEO del segundo factor por intentos fallidos, leído de una respuesta.
 *
 * Contrato de banca (todos los step-ups y el login 2FA):
 *   HTTP 429 · cabecera `Retry-After` ·
 *   `{error, detail: "totp_locked", error_code: "TOTP_LOCKED", retry_after: <segundos>}`
 *
 * TypeScript puro: lo usan el interceptor que publica el bloqueo, la escalera de
 * `ErrorCopyService` y el mapper de Cards, y los tres tienen que leer lo MISMO.
 *
 * Se reconoce por `error_code` (y por `detail`, que también es un código), NUNCA
 * por el texto de `error`. Y un 429 sin esos códigos NO es este bloqueo: puede
 * ser el throttle de la vista (`RATE_LIMITED`), que se dice distinto.
 */
export interface TotpLock {
  /** Segundos que faltan para poder reintentar; `null` si banca no los dijo. */
  readonly retryAfterSeconds: number | null;
}

export const TOTP_LOCKED_ERROR_CODE = 'TOTP_LOCKED';

interface TotpLockBody {
  readonly error_code?: unknown;
  readonly detail?: unknown;
  readonly retry_after?: unknown;
}

export function readTotpLock(
  status: number,
  body: unknown,
  retryAfterHeader: string | null | undefined,
): TotpLock | null {
  if (status !== 429) return null;
  const data = (body && typeof body === 'object' ? body : {}) as TotpLockBody;
  const isLock = data.error_code === TOTP_LOCKED_ERROR_CODE || data.detail === 'totp_locked';
  if (!isLock) return null;
  return { retryAfterSeconds: positiveSeconds(data.retry_after) ?? positiveSeconds(retryAfterHeader) };
}

/** Minutos a esperar, redondeados HACIA ARRIBA: "1 min" con 61 s invita a fallar. */
export function lockMinutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/** Lo que no sea un número positivo es "no se sabe": el aviso sale sin número. */
function positiveSeconds(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : null;
}
